import React, { Fragment, useEffect, useState } from 'react'
import { CommerceEnvironmentalAttributesOfAPart } from '../../../types/commerceApi'
import { Box, Text } from 'theme-ui'
import withData from '../../../enhancers/withData'
import { Datasource, ImageField, TextField } from '../../../types/layoutService'
import AnchorMenuTarget from '../AnchorMenu/AnchorMenuTarget'
import { getTextFieldValue } from '../../../helpers/layoutServiceHelper'
import PartMobileModalLink from '../PartMobileModalLink/PartMobileModalLink'
import useBreakpoints from '../../../hooks/useBreakpoints'
import { BREAKPOINT_M } from '../../../constants/themeConstants'
import SitecoreImage from '../../atoms/SitecoreImage/SitecoreImage'

interface PartEnvironmentalInfoProps {
  partSpecifications: {
    partEnvironmentalAttributes: CommerceEnvironmentalAttributesOfAPart[]
  }
}

interface PartEnvironmentalInfoDatasource {
  title: TextField
  gasUnderPressureIcon: ImageField
  explosiveIcon: ImageField
  oxidisingIcon: ImageField
  flammableIcon: ImageField
  corrosiveIcon: ImageField
  healthHazardIcon: ImageField
  acuteToxicityIcon: ImageField
  seriousHealthHazardIcon: ImageField
  hazardousToTheEnviromentIcon: ImageField
}

const PartEnvironmentalInfo = ({
  rendering: { uid },
  partSpecifications,
  datasource,
}: Datasource<PartEnvironmentalInfoDatasource> & PartEnvironmentalInfoProps) => {
  const { title } = datasource
  const [isClient, setIsClient] = useState(false)
  useEffect(() => {
    setIsClient(true)
  }, [])
  const [breakpointM] = useBreakpoints(BREAKPOINT_M)

  const getIcon = (key: string) => {
    switch (key) {
      case 'Gas under pressure':
        return datasource.gasUnderPressureIcon
      case 'Explosive':
        return datasource.explosiveIcon
      case 'Oxidising':
        return datasource.oxidisingIcon
      case 'Flammable':
        return datasource.flammableIcon
      case 'Corrosive':
        return datasource.corrosiveIcon
      case 'Health Hazard':
        return datasource.healthHazardIcon
      case 'Acute toxicity':
        return datasource.acuteToxicityIcon
      case 'Serious health hazard':
        return datasource.seriousHealthHazardIcon
      case 'Hazardous to the environment':
        return datasource.hazardousToTheEnviromentIcon
      default:
        return undefined
    }
  }

  const Table = () => (
    <Box
      id={uid}
      as="dl"
      sx={{
        display: 'grid',
        gridTemplateColumns: ['auto auto', null, '1fr 1fr'],
        columnGap: 2,
      }}
    >
      {partSpecifications?.partEnvironmentalAttributes.map((item) => (
        <Fragment key={`${item.id}`}>
          <Box
            sx={{
              gridColumn: '1 / -1',
              borderBlockEnd: '1px solid',
              borderColor: 'gray.1',
              display: 'grid',
              gridTemplateColumns: 'subgrid',
              paddingBlock: 2,
            }}
          >
            <Text
              as="dt"
              variant="bodyContent"
              color="gray.3"
              sx={{
                fontWeight: 'bold',
              }}
            >
              {item.description}
            </Text>

            <Box
              as="dd"
              sx={{
                textAlign: ['end', null, 'start'],
              }}
            >
              {item.propertyCode === 'HazardPictogram' ? (
                <>
                  <SitecoreImage
                    imageField={getIcon(item.value)}
                    width={36}
                    height={36}
                    sx={{
                      inlineSize: 36,
                    }}
                  />
                </>
              ) : (
                <Text as="span" variant="bodyContent">
                  {item.value}
                </Text>
              )}
            </Box>
          </Box>
        </Fragment>
      ))}
    </Box>
  )

  if (!breakpointM && isClient) {
    return <PartMobileModalLink title={getTextFieldValue(title)} table={Table()} />
  }

  return (
    <>
      <AnchorMenuTarget title={getTextFieldValue(title)} id={uid} mb={7}>
        {title && (
          <Text as="h2" variant="heading2" mb={4}>
            {getTextFieldValue(title)}
          </Text>
        )}

        {Table()}
      </AnchorMenuTarget>
    </>
  )
}

export default withData(PartEnvironmentalInfo, {
  showMessageWhenPageEditing: true,
})
